import { CalendarIcon, ClockIcon, CloseIcon } from "@xola/icons";
import { DateFormat, PurchaseItemStatus } from "common";
import { Dayjs } from "dayjs";
import { isArray } from "lodash";
import React from "react";
import { Audit, Experience, ExperiencePurchaseItem, Purchase, PurchaseItem } from "types";
import { useResource } from "../../../../hooks/useResource";
import { NoTranslate } from "../../../Common/NoTranslate";
import { ProductImage } from "../../../Common/ProductImage";
import { TimelineItem } from "../TimelineItem";

interface CancelTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

export const CancelTimelineItem = ({ purchase, item, audit, showDate = false }: CancelTimelineItemProps) => {
    if (!CancelTimelineItem.supportsAudit(purchase, item, audit)) {
        return null;
    }

    return (
        <TimelineItem
            title="Canceled"
            audit={audit}
            icon={<CloseIcon />}
            color="danger"
            showDate={showDate}
            purchase={purchase}
        />
    );
};

CancelTimelineItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    return !!getExperiences(item, audit);
};

const getExperiences = (item: PurchaseItem, audit: Audit) => {
    const match: any =
        isArray(audit.data?.items) &&
        audit.data?.items?.find((index: any) => {
            return index.id === item?.id && index.status?.new === PurchaseItemStatus.CANCELED;
        });

    if (match) {
        return [
            <ExperienceBanner
                key={0}
                item={item as ExperiencePurchaseItem}
                experienceId={item.product.id}
                arrivalDateTime={(item as ExperiencePurchaseItem).arrivalDateTime}
            />,
        ];
    }

    return null;
};

interface ExperienceBannerProps {
    item: ExperiencePurchaseItem;
    experienceId: string;
    arrivalDateTime: Dayjs;
}

const ExperienceBanner = ({ item, experienceId, arrivalDateTime }: ExperienceBannerProps) => {
    const { data: product } = useResource<Experience>(["products", experienceId]);
    return (
        <div className="flex flex-row">
            <ProductImage product={{ id: experienceId }} size="small" />
            <div className="flex flex-col justify-between py-1">
                <NoTranslate as="div" className="font-bold" data-testid="cancel-timeline-item-product-name">
                    {product?.name}
                </NoTranslate>
                <div className="flex space-x-2">
                    {arrivalDateTime && (
                        <span className="flex items-center space-x-1">
                            <CalendarIcon />
                            <span data-testid="banner-arrival-date">{arrivalDateTime.format(DateFormat.DATE)}</span>
                        </span>
                    )}
                    {item.arrivalTime ? (
                        <span className="flex items-center space-x-1 pr-1">
                            <ClockIcon />
                            <span data-testid="banner-arrival-time">{arrivalDateTime.format(DateFormat.TIME)}</span>
                        </span>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
